import type { PathParameters, RouteWithChildren } from '../../types';

export type TypeConstrainedParameterList<Path extends string> = keyof {
    [ParameterName in PathParameters<Path>]: string;
} extends never
    ? []
    : [{ [ParameterName in PathParameters<Path>]: string }];

const hasParameters = <Path extends string>(
    args: unknown[],
): args is [{ [ParameterName in PathParameters<Path>]: string }] => args.length === 1;

/**
 * Given an URL with path parameters (ie. '/path/to/{something}')
 * and an dictionary from the name of the parameter to it's value (ie. { something: 'itemId' })
 *
 * This function will replace the URL parameter flag with it's corresponding value
 *      (ie. '/path/to/itemId')
 */
const hydrateUrlParametersFactory =
    <Route extends string>() =>
    <Path extends RouteWithChildren<'', Route>>(path: Path, ...args: TypeConstrainedParameterList<Path>) => {
        if (!hasParameters(args)) {
            return path;
        }

        const [pathParameters] = args;

        return Object.entries(pathParameters as Record<string, string>).reduce(
            (str, [prop, value]) => str.replaceAll(`{${prop}}`, value),
            path as string,
        );
    };

export default hydrateUrlParametersFactory;
